import { Controller } from "@hotwired/stimulus"

const SVG_NS = "http://www.w3.org/2000/svg"
const CARRIED_STATES = ["ok", "ng", "dim", "show"]
const STATE_CLASSES = ["is-focus", "is-ok", "is-ng", "is-dim", "is-show", "is-active", "is-arrived", "is-check", "is-completed"]
const PACKET_RADIUS = 6
const PACKET_LINGER = 450
const AUTOPLAY_THRESHOLD = 0.45

// ```diagram の図（app/lib/diagram/renderer.rb）をシナリオの段ごとに再生する。doc/design/ingest.html の makePlayer と同じ規則で、
// ok / ng / dim / show は次の段へ持ち越し、clear で外す。focus と flow と check はその段だけ
export default class extends Controller {
  static targets = ["panel", "scenarios", "scenario", "scenarioButton", "playButton", "narration", "count", "packets"]
  static values = { marker: String }

  #scenarios = []
  #scenarioIndex = 0
  #stepIndex = -1
  #playing = false
  #timer
  #frame
  #packets = []
  #observer
  #idleHtml
  #reducedMotion

  connect() {
    // word-insight のトップの fragment cache には、check を持たない版の段が残る
    this.#scenarios = this.scenarioTargets.map(scenario =>
      [...scenario.querySelectorAll("li[data-step]")].map(item => ({ check: [], ...JSON.parse(item.dataset.step), html: item.innerHTML })),
    )
    this.#reducedMotion = matchMedia("(prefers-reduced-motion: reduce)").matches
    this.#idleHtml ??= this.narrationTarget.innerHTML
    this.packetsTargets.forEach(packets => packets.replaceChildren())
    this.panelTarget.hidden = false
    this.scenariosTarget.hidden = true
    this.#render(-1)
    if (this.#reducedMotion) return

    this.#observer = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return

      this.#observer.disconnect()
      if (this.#stepIndex < 0 && !this.#playing) this.#play()
    }, { threshold: AUTOPLAY_THRESHOLD })
    this.#observer.observe(this.element)
  }

  // 同じ要素がつなぎ直されると Stimulus は同じ controller を使い回すので、再生中の印も戻す
  disconnect() {
    this.#observer?.disconnect()
    clearTimeout(this.#timer)
    this.#playing = false
    this.#resetParts()
  }

  choose({ params: { scenario } }) {
    this.#pause()
    this.#scenarioIndex = scenario
    this.#stepIndex = -1
    this.#play()
  }

  toggle() {
    this.#playing ? this.#pause() : this.#play()
  }

  next() {
    this.#pause()
    if (!this.#atLastStep) this.#render(this.#stepIndex + 1)
  }

  previous() {
    this.#pause()
    if (this.#stepIndex >= 0) this.#render(this.#stepIndex - 1)
  }

  reset() {
    this.#pause()
    this.#render(-1)
  }

  // 縮め終えたか、途中で svg が隠れて animation が取り消されたら（画面を回す）、大きな印を文言の前の印に替える。is-check を
  // 残すと、出し直したときに CSS の animation が最初から走る。文言の前の印は両方の svg に出し、画面を回しても揃える。
  // 段を移って is-check を外したことによる取り消しでは替えない
  finishCheck({ currentTarget }) {
    if (!currentTarget.classList.contains("is-check")) return

    currentTarget.classList.remove("is-check")
    this.#addClass(currentTarget.dataset.diagramCheck, "is-completed")
  }

  get #steps() {
    return this.#scenarios[this.#scenarioIndex]
  }

  get #atLastStep() {
    return this.#stepIndex >= this.#steps.length - 1
  }

  get #playLabel() {
    if (this.#playing) return "一時停止"
    return this.#atLastStep ? "もう一度" : "再生"
  }

  #play() {
    clearTimeout(this.#timer)
    if (this.#atLastStep) this.#stepIndex = -1
    this.#playing = true
    this.#advance()
  }

  #pause() {
    clearTimeout(this.#timer)
    this.#playing = false
    this.#syncButtons()
  }

  #advance() {
    this.#render(this.#stepIndex + 1)
    this.#timer = setTimeout(() => {
      if (this.#atLastStep) {
        this.#playing = false
        this.#syncButtons()
      } else {
        this.#advance()
      }
    }, this.#steps[this.#stepIndex].duration)
  }

  #render(index) {
    this.#resetParts()
    this.#stepIndex = index
    if (index < 0) {
      this.narrationTarget.innerHTML = this.#idleHtml
      this.countTarget.textContent = ""
      this.#syncButtons()
      return
    }

    this.#carriedStates(index).forEach((ids, state) => ids.forEach(id => this.#addClass(id, `is-${state}`)))
    const step = this.#steps[index]
    step.focus.forEach(id => this.#addClass(id, "is-focus"))
    step.flow.forEach(flow => this.#flow(flow))
    step.check.forEach(id => this.#check(id))
    // 説明文の HTML はサーバーが FinalSanitizer に通したもの（HtmlPipeline::Filters::Diagram#render_caption）
    this.narrationTarget.innerHTML = step.html
    this.countTarget.textContent = `${index + 1} / ${this.#steps.length}`
    this.#syncButtons()
  }

  #carriedStates(index) {
    const carried = new Map(CARRIED_STATES.map(state => [state, new Set()]))
    this.#steps.slice(0, index + 1).forEach(step => {
      step.clear.forEach(id => carried.forEach(ids => ids.delete(id)))
      CARRIED_STATES.forEach(state => step[state].forEach(id => carried.get(state).add(id)))
    })
    return carried
  }

  #syncButtons() {
    this.scenarioButtonTargets.forEach((button, index) => {
      button.setAttribute("aria-pressed", String(index === this.#scenarioIndex && this.#stepIndex >= 0))
    })
    this.playButtonTarget.textContent = this.#playLabel
  }

  // narrow（狭い画面用の配置）を書いた図は svg が 2 枚あり、同じ id の部品が両方にある。印は両方に付け、枠の幅が変わっても揃える
  #parts(id, root = this.element) {
    return root.querySelectorAll(`[data-diagram-id="${CSS.escape(id)}"]`)
  }

  #addClass(id, name) {
    this.#parts(id).forEach(part => part.classList.add(name))
  }

  #flow(flow) {
    this.#parts(flow.edge).forEach(path => {
      path.classList.add("is-active")
      this.#pointMarkers(path, `${this.#markerId(path)}-active`)
      if (!this.#reducedMotion && this.#rendered(path.ownerSVGElement)) this.#spawn(path, flow)
    })
  }

  // 完了の印を描くのは段に入ったときの 1 回きりなので、is-check は見えている svg の印にだけ付ける（#arrive と同じ）。
  // 動きを減らす設定では大きな印を描かず、文言の前の印だけをすぐに出す
  #check(id) {
    if (this.#reducedMotion) {
      this.#addClass(id, "is-completed")
      return
    }

    this.element.querySelectorAll(`[data-diagram-check="${CSS.escape(id)}"]`).forEach(check => {
      if (this.#rendered(check.ownerSVGElement)) check.classList.add("is-check")
    })
  }

  // Safari は描かれていない（display: none の svg の中の）path の getPointAtLength で InvalidStateError を投げる
  #rendered(svg) {
    return svg.getClientRects().length > 0
  }

  // app/lib/diagram/renderer.rb は narrow の svg に、figure の marker の id に -narrow を足した id の marker を持たせる
  #markerId(path) {
    return path.closest(".diagram-svg-narrow") ? `${this.markerValue}-narrow` : this.markerValue
  }

  #pointMarkers(path, markerId) {
    for (const attribute of ["marker-end", "marker-start"]) {
      if (path.hasAttribute(attribute)) path.setAttribute(attribute, `url(#${markerId})`)
    }
  }

  #spawn(path, { tone, duration, delay, reverse }) {
    const circle = document.createElementNS(SVG_NS, "circle")
    circle.setAttribute("r", PACKET_RADIUS)
    circle.setAttribute("class", `diagram-packet diagram-packet-${tone}`)
    circle.style.opacity = 0
    this.packetsTargets.find(packets => path.ownerSVGElement.contains(packets)).append(circle)
    const arrivalId = reverse ? path.dataset.diagramFrom : path.dataset.diagramTo
    const start = performance.now() + delay
    this.#packets.push({ circle, path, arrivalId, arrived: false, length: path.getTotalLength(), start, duration, reverse })
    this.#frame ??= requestAnimationFrame(this.#tick)
  }

  #tick = now => {
    const hidden = new Set(this.#packets.map(packet => packet.path.ownerSVGElement).filter(svg => !this.#rendered(svg)))
    this.#packets = this.#packets.filter(packet => {
      const elapsed = now - packet.start
      if (elapsed < 0) return true
      const shown = !hidden.has(packet.path.ownerSVGElement)
      // 届いた後の残りの時間にフレームが来なくても（背面のタブ・重い処理）、届いたことにしてから点を捨てる
      if (shown && elapsed >= packet.duration && !packet.arrived) this.#arrive(packet)
      if (elapsed > packet.duration + PACKET_LINGER || !shown) {
        packet.circle.remove()
        return false
      }

      const progress = Math.min(elapsed / packet.duration, 1)
      const point = packet.path.getPointAtLength(packet.length * (packet.reverse ? 1 - progress : progress))
      packet.circle.setAttribute("cx", point.x)
      packet.circle.setAttribute("cy", point.y)
      packet.circle.style.opacity = 1
      return true
    })
    this.#frame = this.#packets.length > 0 ? requestAnimationFrame(this.#tick) : undefined
  }

  // 届いたことは段の状態ではなく 1 回きりの出来事なので、点が走った svg にだけ印を付ける（隠れている側に付けると、表示されたときに跳ねる）
  #arrive(packet) {
    packet.arrived = true
    this.#parts(packet.arrivalId, packet.path.ownerSVGElement).forEach(part => part.classList.add("is-arrived"))
  }

  #resetParts() {
    this.element.querySelectorAll(".diagram-edge.is-active").forEach(path => this.#pointMarkers(path, this.#markerId(path)))
    this.element.querySelectorAll(STATE_CLASSES.map(name => `.${name}`).join(",")).forEach(part => part.classList.remove(...STATE_CLASSES))
    cancelAnimationFrame(this.#frame)
    this.#frame = undefined
    this.#packets.forEach(packet => packet.circle.remove())
    this.#packets = []
  }
}
